/* eslint-disable @typescript-eslint/no-explicit-any */

import { getBenefitTypes } from '@ben/columbia/api/get-benefit-types';
import { useColumbiaErrors } from '@ben/columbia/contexts';
import type { AirtableFetchData } from '@ben/columbia/hooks/use-mutation-airtable-fetch';
import { useQueryScheduledVersion } from '@ben/columbia/hooks/use-query-scheduled-version';
import { useFormatDate } from '@ben/common/date';
import { Modal } from '@ben/orion/modals';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import { UnstyledFieldset } from '@ben/orion/fieldset';
import Loader from '@ben/orion/loader';
import type { IModularBenefitDetail, ITag } from '@ben/shared/types/columbia';
import { useIntersection } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import type { FC, FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { isNumber } from 'lodash';
import styled from 'styled-components';
import { useAirtableState } from '../airtable-header';
import AirtableHeaderElement from './elements/airtable-header-element';
import BenefitDocumentsElement from './elements/benefit-documents-element';
import BenefitFeaturesElement from './elements/benefit-features-element';
import ConditionalApprovalsElement from './elements/conditional-approvals-element';
import EligibilityRulesElement from './elements/eligibility-rules-element';
import EnrollmentConfigElement from './elements/enrollment-config-element';
import EnrollmentSettingsElement from './elements/enrollment-settings-element';
import EnrollmentWindowsElement from './elements/enrollment-windows-element';
import GeneralInformationElement from './elements/general-information-element';
import InternalNotesElement from './elements/internal-notes-element';
import LifeEventsElement from './elements/life-events-element';
import ManagerAndProviderElement from './elements/manager-and-provider-element';
import ModbenBenefitImageElement from './elements/modben-benefit-image-element';
import ModbenHeroImageElement from './elements/modben-hero-image-element';
import PayrollSettingsElement from './elements/payroll-settings-element';
import PricesElement from './elements/prices-element';
import RateTableElement from './elements/rate-table-element';
import ReportingSectionElement from './elements/reporting-section-element';
import StepsElement from './elements/steps-element';
import { SectionContainer } from './elements/styles';
import type { SectionFormPartial } from './elements/types';
import useBenefitSectionForms from './hooks/use-benefit-section-forms';
import SectionNavigation from './section-navigation/section-navigation';
import buildMergedBenefit from './services/build-merged-benefit';
import UnsavedChangesBar from './unsaved-changes-bar';
import {
  buildReadableErrorPath,
  findFirstErrorPath,
  sanitizePayload,
} from './utils';

const BASE_SECTIONS = [
  { id: 'general-information', title: 'General Information' },
  { id: 'enrolment-settings', title: 'Enrolment Settings' },
  { id: 'enrolment-config', title: 'Enrolment Config' },
  { id: 'steps', title: 'Steps' },
  { id: 'conditional-approvals', title: 'Conditional Approvals' },
  { id: 'benefit-windows', title: 'Benefit windows' },
  { id: 'life-events', title: 'Life Events' },
  { id: 'manager-and-provider', title: 'Manager and provider' },
  { id: 'rate-table', title: 'Rate Table' },
  { id: 'benefit-image', title: 'Benefit Image' },
  { id: 'benefit-hero-image', title: 'Hero Image' },
  { id: 'benefit-documents', title: 'Benefit Documents' },
  { id: 'benefit-features', title: 'Benefit Features' },
  { id: 'eligibility-rules', title: 'Eligibility Rules' },
  { id: 'pricing', title: 'Pricing' },
  { id: 'payroll-settings', title: 'Payroll Settings' },
  { id: 'reporting', title: 'Reporting' },
  { id: 'internal-notes', title: 'Internal Notes' },
] as const;

interface IBenefitConfigProps {
  benefit: IModularBenefitDetail;
  onSave: (currentValues: IModularBenefitDetail) => Promise<void>;
  onDelete: () => void;
  isCloneBenefit: boolean;
  isScheduledVersion: boolean;
}

const BenefitConfig: FC<IBenefitConfigProps> = ({
  benefit,
  onSave,
  onDelete,
  isCloneBenefit,
  isScheduledVersion,
}) => {
  const { errors } = useColumbiaErrors();
  const formatDate = useFormatDate();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEnrollmentDateNoneModalOpen, setIsEnrollmentDateNoneModalOpen] =
    useState(false);
  const [isBenefitImageUploadInProgress, setIsBenefitImageUploadInProgress] =
    useState(false);
  const [isHeroImageUploadInProgress, setIsHeroImageUploadInProgress] =
    useState(false);
  const [isDocumentUploadInProgress, setIsDocumentUploadInProgress] =
    useState(false);
  const isUploadInprogress =
    isBenefitImageUploadInProgress ||
    isHeroImageUploadInProgress ||
    isDocumentUploadInProgress;
  const [airtableState, setAirtableState] = useAirtableState();

  const { ref: saveBarRef, entry } = useIntersection({
    root: undefined,
    threshold: 1,
  });

  const isSharedBenefit = !!benefit.sharedBenefit;

  const { data: scheduledVersion } = useQueryScheduledVersion(
    String(benefit?.id),
    { enabled: !!benefit?.id && !isCloneBenefit }
  );

  const { data: benefitTypes } = useQuery(['benefit-types'], getBenefitTypes);

  const sectionForms = useBenefitSectionForms(benefit);
  const sectionFormList = Object.values(sectionForms);
  const {
    airtableForm,
    generalForm,
    enrolmentSettingsForm,
    enrolmentConfigForm,
    windowsForm,
    lifeEventsForm,
    managerForm,
    rateTableForm,
    pricesForm,
    payrollForm,
    reportingForm,
    conditionalApprovalsForm,
    benefitImageForm,
    benefitHeroImageForm,
    benefitDocumentsForm,
    benefitFeaturesForm,
    eligibilityRulesForm,
    internalNotesForm,
    stepsForm,
  } = sectionForms;

  const employerId = generalForm.watch('employer');

  useEffect(() => {
    if (isNumber(employerId)) {
      enrolmentConfigForm.setValue('employer', employerId, {
        shouldDirty: false,
      });
      eligibilityRulesForm.setValue('employer', employerId, {
        shouldDirty: false,
      });
    }
  }, [employerId, enrolmentConfigForm, eligibilityRulesForm]);

  interface SaveOptions {
    bypassEnrollmentNoneCheck?: boolean;
    bypassScheduledVersionCheck?: boolean;
  }

  const handleSave = async (event?: FormEvent, options: SaveOptions = {}) => {
    event?.preventDefault();
    const {
      bypassEnrollmentNoneCheck = false,
      bypassScheduledVersionCheck = false,
    } = options;

    const subPricesNeedTesting =
      pricesForm.getValues()?.additionalConfig?.subPricesNeedTesting;

    if (subPricesNeedTesting) {
      toast.error(
        'Pricing formulas have not been tested yet. Test formulas to proceed.'
      );
      return;
    }

    const enrollmentConfig = enrolmentConfigForm.getValues('enrollmentConfig');

    const isStartDateNone =
      !enrollmentConfig?.dynamicEnrollmentStartDate &&
      !enrollmentConfig?.fixedEnrollmentStartDate;

    const isEndDateNone =
      !enrollmentConfig?.fixedEnrollmentEndDate &&
      !enrollmentConfig?.dynamicEnrollmentEndDate &&
      !enrollmentConfig?.manualEnrollmentEndDate;

    if (
      (isStartDateNone || isEndDateNone) &&
      !benefit?.id &&
      !bypassEnrollmentNoneCheck
    ) {
      setIsEnrollmentDateNoneModalOpen(true);
      return;
    }

    if (
      scheduledVersion?.enrollableBenefit &&
      !bypassScheduledVersionCheck &&
      !isCloneBenefit
    ) {
      setIsConfirmModalOpen(true);
      return;
    }

    const formsWithLabels = [
      { form: airtableForm, label: 'Airtable' },
      { form: generalForm, label: 'General information' },
      { form: enrolmentSettingsForm, label: 'Enrollment settings' },
      { form: enrolmentConfigForm, label: 'Enrollment configuration' },
      { form: stepsForm, label: 'Steps' },
      { form: conditionalApprovalsForm, label: 'Conditional approvals' },
      { form: windowsForm, label: 'Enrollment windows' },
      { form: lifeEventsForm, label: 'Life events' },
      { form: managerForm, label: 'Manager and provider' },
      { form: rateTableForm, label: 'Rate tables' },
      { form: pricesForm, label: 'Pricing' },
      { form: payrollForm, label: 'Payroll settings' },
      { form: reportingForm, label: 'Reporting' },
      { form: benefitImageForm, label: 'Benefit image' },
      { form: benefitHeroImageForm, label: 'Benefit hero image' },
      { form: benefitDocumentsForm, label: 'Benefit documents' },
      { form: benefitFeaturesForm, label: 'Benefit features' },
      { form: eligibilityRulesForm, label: 'Eligibility rules' },
      { form: internalNotesForm, label: 'Internal notes' },
    ] as { form: SectionFormPartial<any>; label: string }[];

    const validationResults = await Promise.all(
      formsWithLabels.map(async ({ form, label }) => ({
        label,
        valid: await form.trigger(undefined, { shouldFocus: false }),
      }))
    ).catch(() => [] as { label: string; valid: boolean }[]);

    const invalidLabels = validationResults
      .filter((result) => !result.valid)
      .map((result) => result.label);

    if (invalidLabels.length > 0) {
      const firstInvalid = formsWithLabels.find(
        ({ label }) => label === invalidLabels[0]
      );
      if (firstInvalid) {
        await firstInvalid.form.trigger(undefined, { shouldFocus: true });
        const firstErrorPath = findFirstErrorPath(
          firstInvalid.form.formState.errors
        );
        if (firstErrorPath) {
          const readablePath = buildReadableErrorPath(firstErrorPath, [
            'additionalConfig',
          ]);

          toast.error(
            `Validation failed in ${firstInvalid.label}. First error: ${readablePath}`
          );
          return;
        }
      }
      return;
    }

    const merged = buildMergedBenefit(benefit, {
      sectionForms: sectionFormList as any,
      stepsForm: stepsForm,
      rateTableForm: rateTableForm,
      generalForm: generalForm,
      conditionalApprovalsForm: conditionalApprovalsForm,
      documentsForm: benefitDocumentsForm,
      eligibilityRulesForm: eligibilityRulesForm,
    });

    try {
      await onSave(sanitizePayload(merged) as IModularBenefitDetail);
      for (const form of sectionFormList) {
        form.reset(merged, { keepDirtyValues: false });
      }
    } catch {
      // API error — keep all dirty field state intact so the user's changes
      // are not lost and the next save attempt sends the full diff again.
    }
  };

  const handleTagsValueChange = useCallback(
    (value: ITag[]) => {
      reportingForm.setValue('additionalConfig.tags', value, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    },
    [reportingForm]
  );

  const reportingDefaultValue = useMemo(() => {
    return benefit?.additionalConfig?.tags?.map(({ id }) => String(id)) ?? [];
  }, [benefit.additionalConfig?.tags]);

  const memoWarnings = useMemo(
    () => airtableState.warnings,
    [airtableState.warnings]
  );
  const memoSuccesses = useMemo(
    () => airtableState.successes,
    [airtableState.successes]
  );
  const memoErrors = useMemo(
    () => (errors ?? {}) as Record<string, boolean>,
    [errors]
  );

  const tagsSelectProps = useMemo(
    () => ({
      onChange: (values: ITag[]) => handleTagsValueChange(values),
      defaultValue: reportingDefaultValue,
      hasError: !!errors?.tags,
    }),
    [handleTagsValueChange, reportingDefaultValue, errors?.tags]
  );

  const handleAirtableSuccess = useCallback(
    (data: AirtableFetchData, _airtableId: string) => {
      setAirtableState(data);
    },
    [setAirtableState]
  );

  const handleAirtableError = useCallback(() => {
    setAirtableState(undefined);
  }, [setAirtableState]);

  const steps = useWatch({
    control: stepsForm.control,
    name: 'additionalConfig.steps',
    defaultValue: [],
  });

  const sections = useMemo(() => {
    return BASE_SECTIONS.map((section) => {
      if (section.id === 'steps' && Array.isArray(steps) && steps.length > 0) {
        return {
          ...section,
          children: steps.map((step, index) => ({
            id: `step-${index}`,
            title: step?.name || `Step #${index + 1}`,
          })),
        };
      }
      return section;
    });
  }, [steps]);

  if (Object.keys(benefit).length === 0) {
    return <Loader variant="full-height" />;
  }

  return (
    <>
      <SectionNavigation sections={sections} />
      <form
        onSubmit={(event) => {
          void handleSave(event).catch(console.error);
        }}
      >
        <UnstyledFieldset disabled={isSharedBenefit}>
          <AirtableHeaderElement
            form={airtableForm}
            isScheduledVersion={isScheduledVersion}
            onSuccess={handleAirtableSuccess}
            onError={handleAirtableError}
          />
          <SectionContainer id="general-information">
            <GeneralInformationElement
              form={generalForm}
              errors={memoErrors}
              warnings={memoWarnings}
              successes={memoSuccesses}
              benefitTypes={benefitTypes ?? []}
              isScheduledVersion={isScheduledVersion}
            />
          </SectionContainer>
          <SectionContainer id="enrolment-settings">
            <EnrollmentSettingsElement
              form={enrolmentSettingsForm}
              warnings={memoWarnings}
              successes={memoSuccesses}
            />
          </SectionContainer>
          <SectionContainer id="enrolment-config">
            <EnrollmentConfigElement form={enrolmentConfigForm} />
          </SectionContainer>
          <SectionContainer id="steps">
            <StepsElement
              form={stepsForm}
              isScheduledVersion={isScheduledVersion}
            />
          </SectionContainer>
          <SectionContainer id="conditional-approvals">
            <ConditionalApprovalsElement form={conditionalApprovalsForm} />
          </SectionContainer>
          <SectionContainer id="benefit-windows">
            <EnrollmentWindowsElement
              form={windowsForm}
              errors={memoErrors}
              successes={memoSuccesses}
            />
          </SectionContainer>
          <SectionContainer id="life-events">
            <LifeEventsElement
              form={lifeEventsForm}
              stepsForm={stepsForm}
              errors={memoErrors}
              successes={memoSuccesses}
            />
          </SectionContainer>
          <SectionContainer id="manager-and-provider">
            <ManagerAndProviderElement
              form={managerForm}
              errors={memoErrors}
              warnings={memoWarnings}
              successes={memoSuccesses}
              isScheduledVersion={isScheduledVersion}
            />
          </SectionContainer>
          <SectionContainer id="rate-table">
            <RateTableElement form={rateTableForm} />
          </SectionContainer>
          <SectionContainer id="benefit-image">
            <ModbenBenefitImageElement
              form={benefitImageForm}
              isCloneBenefit={isCloneBenefit}
              setIsUploadInProgress={setIsBenefitImageUploadInProgress}
            />
          </SectionContainer>
          <SectionContainer id="benefit-hero-image">
            <ModbenHeroImageElement
              form={benefitHeroImageForm}
              isCloneBenefit={isCloneBenefit}
              setIsUploadInProgress={setIsHeroImageUploadInProgress}
            />
          </SectionContainer>
          <SectionContainer id="benefit-documents">
            <BenefitDocumentsElement
              form={benefitDocumentsForm}
              isCloneBenefit={isCloneBenefit}
              setIsUploadInProgress={setIsDocumentUploadInProgress}
            />
          </SectionContainer>
          <SectionContainer id="benefit-features">
            <BenefitFeaturesElement
              form={benefitFeaturesForm}
              benefitId={benefit.id?.toString()}
            />
          </SectionContainer>
          <SectionContainer id="eligibility-rules">
            <EligibilityRulesElement
              form={eligibilityRulesForm}
              isScheduledVersion={isScheduledVersion}
            />
          </SectionContainer>
          <SectionContainer id="pricing">
            <PricesElement
              form={pricesForm}
              stepsForm={stepsForm}
              rateTableForm={rateTableForm}
              errors={memoErrors}
              warnings={memoWarnings}
              successes={memoSuccesses}
            />
          </SectionContainer>
          <SectionContainer id="payroll-settings">
            <PayrollSettingsElement form={payrollForm} />
          </SectionContainer>
          <SectionContainer id="reporting">
            <ReportingSectionElement
              form={reportingForm}
              tagsSelectProps={tagsSelectProps}
            />
          </SectionContainer>
          <SectionContainer id="internal-notes">
            <InternalNotesElement form={internalNotesForm} />
          </SectionContainer>
          <SectionContainer>
            <ButtonContainer ref={saveBarRef}>
              <Button
                label="Delete"
                variant={ButtonVariant.secondary}
                onClick={onDelete}
                width="180px"
                disabled={isCloneBenefit}
              />
              <Button
                label="Save"
                type="submit"
                onClick={(event) => {
                  void handleSave(event).catch(console.error);
                }}
                variant={ButtonVariant.primary}
                disabled={isUploadInprogress}
                width="180px"
              />
            </ButtonContainer>
          </SectionContainer>
        </UnstyledFieldset>
      </form>
      <UnsavedChangesBar
        isOpen={
          sectionFormList.some((f) => f.formState.isDirty) &&
          !entry?.isIntersecting
        }
        onReset={() => {
          for (const form of sectionFormList) {
            form.reset(benefit, { keepDirtyValues: false });
          }
        }}
        onSave={() => {
          void handleSave().catch(console.error);
        }}
      />
      {isEnrollmentDateNoneModalOpen && (
        <Modal
          isOpen={isEnrollmentDateNoneModalOpen}
          onClose={() => setIsEnrollmentDateNoneModalOpen(false)}
          onConfirm={() => {
            setIsEnrollmentDateNoneModalOpen(false);
            if (scheduledVersion?.enrollableBenefit && !isCloneBenefit) {
              setIsConfirmModalOpen(true);
            } else {
              void handleSave(undefined, { bypassEnrollmentNoneCheck: true });
            }
          }}
          title="Are you sure?"
          description="Are you sure you want to set the enrollment date type to None?"
          primaryText="Confirm"
          secondaryText="Cancel"
        />
      )}
      {isConfirmModalOpen && (
        <Modal
          isOpen={isConfirmModalOpen}
          title="Changes only apply to active version"
          description={`Your changes won’t be applied to the scheduled version starting on ${formatDate(scheduledVersion?.enrollableBenefit?.scheduledRenewalDate)}.`}
          primaryText="Save"
          secondaryText="Cancel"
          onConfirm={() => {
            void handleSave(undefined, { bypassScheduledVersionCheck: true });
            setIsConfirmModalOpen(false);
          }}
          onClose={() => setIsConfirmModalOpen(false)}
        />
      )}
    </>
  );
};

export default BenefitConfig;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
